import type { LeadOrigin, LeadInteraction, ClientContact } from '../../context/ClientManagementContext';
import { useSocialChatWidget, EMPTY_LEAD_ORIGIN } from '../../context/SocialChatWidgetContext';
import { hasUnseenSocialUpdate, markSocialSeen } from '@/shared/utils/social-lead-activity';
import { SocialChatLauncher } from './SocialChatLauncher';


interface LeadOriginSectionProps {
  lead_origin?: LeadOrigin;
  lead_interactions?: LeadInteraction[];
  contacts?: ClientContact[];
  agencyId: string;
  clientId: string;
  clientName?: string;
  onMerged?: () => void;
  /** Preview/test-only escape hatch: skips the real fetchPortalConnection call when set. */
  portalConnectedOverride?: boolean;
}

/**
 * Facebook-Messenger-style bubble fixed to a page corner. Opens this client
 * into the "Social" widget (Messenger | IG Direct | Posts, plus merge),
 * which is actually rendered by SocialChatWidgetRenderer at the agency
 * dashboard layout so it survives navigating away from this page. Renders
 * nothing here once this client is the active widget target — the globally
 * mounted panel is already showing it.
 */
export function LeadOriginSection({
  lead_origin = EMPTY_LEAD_ORIGIN,
  lead_interactions,
  contacts,
  agencyId,
  clientId,
  clientName,
  onMerged,
  portalConnectedOverride,
}: LeadOriginSectionProps) {
  const target = useSocialChatWidget((s) => s.target);
  const openChat = useSocialChatWidget((s) => s.openChat);

  const isActiveHere = target?.agencyId === agencyId && target?.clientId === clientId;
  if (isActiveHere) return null;

  return (
    <SocialChatLauncher
      hasUnseenUpdate={hasUnseenSocialUpdate(clientId, lead_interactions)}
      onClick={() => {
        markSocialSeen(clientId);
        openChat({
          agencyId,
          clientId,
          clientName,
          leadOrigin: lead_origin,
          leadInteractions: lead_interactions,
          contacts,
          onMerged,
          portalConnectedOverride,
        });
      }}
    />
  );
}
